(() => {
  document.documentElement.classList.remove("no-js");
  document.documentElement.classList.add("js");

  const pageType = document.body?.dataset.pageType || "unknown";
  const approvedStorefrontMarkets = new Set(["japan", "thailand", "unknown"]);
  const analyticsPreview = document.documentElement.dataset.analyticsPreview === "true";
  const qaStorageKey = "eagleline_qa_v1";
  const qaCampaigns = new Set(["technical-audit", "eagleline-qa"]);
  let qaMode = analyticsPreview;
  try {
    qaMode ||= new URLSearchParams(window.location.search).getAll("utm_campaign")
      .some((campaign) => qaCampaigns.has(campaign));
  } catch {}
  try {
    qaMode ||= window.sessionStorage.getItem(qaStorageKey) === "1";
    if (qaMode) window.sessionStorage.setItem(qaStorageKey, "1");
  } catch {}
  const declaredStorefrontMarket = document.body?.dataset.storefrontMarket;
  const storefrontMarket = approvedStorefrontMarkets.has(declaredStorefrontMarket)
    ? declaredStorefrontMarket
    : "unknown";
  const openingRoot = document.querySelector("[data-opening-root]");
  if (openingRoot) {
    const allowedOpenings = new Set(["demonstration", "gift", "carry"]);
    const openingValue = new URL(window.location.href).searchParams.get("el_opening") || "";
    const selectedOpening = allowedOpenings.has(openingValue) ? openingValue : "default";
    openingRoot.dataset.opening = selectedOpening;
    const openingCopy = openingRoot.querySelector("[data-opening-copy]");
    const selectedCopy = selectedOpening === "default"
      ? ""
      : openingCopy?.getAttribute(`data-opening-${selectedOpening}`) || "";
    if (openingCopy && selectedCopy) openingCopy.textContent = selectedCopy;
  }
  let themeStrings = {};
  try {
    const themeStringsNode = document.getElementById("EagleLineThemeStrings");
    if (themeStringsNode) themeStrings = JSON.parse(themeStringsNode.textContent);
  } catch {
    themeStrings = {};
  }
  const themeString = (key, fallback = "") => {
    const value = themeStrings[key];
    return typeof value === "string" && value ? value : fallback;
  };

  const promotionCountdowns = [...document.querySelectorAll("[data-promotion-countdown]")];
  if (promotionCountdowns.length) {
    const renderPromotionCountdowns = () => {
      if (document.visibilityState === "hidden") return;
      promotionCountdowns.forEach((countdown) => {
        const endAt = Date.parse(countdown.dataset.promotionCountdownEnd || "");
        const value = countdown.querySelector("[data-promotion-countdown-value]");
        const fallback = countdown.querySelector("[data-promotion-countdown-fallback]");
        const template = countdown.dataset.promotionCountdownTemplate || "";
        if (!Number.isFinite(endAt) || !value || !template) {
          if (value) value.hidden = true;
          if (fallback) fallback.hidden = false;
          return;
        }
        const remainingSeconds = Math.max(0, Math.ceil((endAt - Date.now()) / 1_000));
        const hours = Math.floor(remainingSeconds / 3_600);
        const minutes = Math.floor((remainingSeconds % 3_600) / 60);
        const seconds = remainingSeconds % 60;
        const renderedValue = template
          .replaceAll("__EL_HOURS__", String(hours))
          .replaceAll("__EL_MINUTES__", String(minutes).padStart(2, "0"))
          .replaceAll("__EL_SECONDS__", String(seconds).padStart(2, "0"));
        if (value.textContent !== renderedValue) value.textContent = renderedValue;
        value.hidden = false;
        if (fallback) fallback.hidden = true;
      });
    };
    renderPromotionCountdowns();
    window.setInterval(renderPromotionCountdowns, 1_000);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") renderPromotionCountdowns();
    });
  }

  const publishEagleLineEvent = (name, parameters = {}, onSettled = () => {}) => {
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== "function") {
      onSettled(false);
      return false;
    }
    try {
      const result = publish.call(window.Shopify.analytics, name, {
        ...parameters,
        storefront_market: storefrontMarket,
        ...(qaMode ? { qa_mode: true } : {}),
      });
      // Online-store publish can return void; only an explicit failure is a
      // failure. This is dispatch acknowledgement, not proof of pixel delivery.
      if (result && typeof result.then === "function") {
        result.then((accepted) => onSettled(accepted !== false), () => onSettled(false));
      } else onSettled(result !== false);
      return result !== false;
    } catch {
      onSettled(false);
      return false;
    }
  };

  const TRACKING_STATE_EVENT = "eagleline:tracking-state";
  const CONTENT_STABILITY_MS = 1000;
  const ARRIVAL_ATTRIBUTION_MS = 4000;
  const acceleratedPurchasePlacements = new Set(["hero", "floating_header", "top_header"]);
  const contentSections = [...document.querySelectorAll("[data-content-section]")];
  let activeNavigation = null;
  let settledAttribution = null;
  let directEntryAttribution = null;
  let navigationSettleTimer = 0;
  let attributionExpiryTimer = 0;
  let navigationAnimationFrame = 0;
  let navigationScrollBehavior = null;

  const dispatchTrackingState = () => {
    document.dispatchEvent(new CustomEvent(TRACKING_STATE_EVENT));
  };

  const contentSectionForElement = (element) => {
    const zone = element?.closest?.("[data-content-section]");
    return zone?.dataset.contentSection || null;
  };

  const visibleViewportArea = (element) => {
    if (!element?.isConnected) return 0;
    const rect = element.getBoundingClientRect();
    const width = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0));
    const height = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
    return width * height;
  };

  const dominantContentSection = ({ preferNavigationTarget = true } = {}) => {
    const viewportArea = Math.max(1, window.innerWidth * window.innerHeight);
    const scores = contentSections
      .map((element) => ({
        element,
        sectionName: element.dataset.contentSection,
        area: visibleViewportArea(element),
      }))
      .filter((score) => score.sectionName && score.area > 0)
      .sort((left, right) => right.area - left.area);
    if (!scores.length || scores[0].area < viewportArea * .12) return "transition";

    const preferredSection = preferNavigationTarget
      ? (activeNavigation?.targetSection
        || settledAttribution?.targetSection
        || directEntryAttribution?.targetSection)
      : null;
    if (preferredSection) {
      const preferred = scores.find((score) => score.sectionName === preferredSection);
      if (preferred && preferred.area >= viewportArea * .12) return preferredSection;
    }

    const [first, second] = scores;
    if (second && second.area >= first.area * .9) return "transition";
    return first.sectionName;
  };

  const currentViewAttribution = () => {
    if (activeNavigation) return null;
    if (settledAttribution) {
      return {
        arrival_method: "assisted",
        navigation_source: settledAttribution.navigationSource,
      };
    }
    if (directEntryAttribution) {
      return {
        arrival_method: "direct_entry",
        navigation_source: "direct_hash",
      };
    }
    return { arrival_method: "organic", navigation_source: "none" };
  };

  const clearAttribution = () => {
    window.clearTimeout(attributionExpiryTimer);
    attributionExpiryTimer = 0;
    settledAttribution = null;
    directEntryAttribution = null;
  };

  const finishNavigation = () => {
    if (!activeNavigation) return;
    const completed = activeNavigation;
    activeNavigation = null;
    if (completed.arrivalMethod === "direct_entry") {
      directEntryAttribution = { targetSection: completed.targetSection };
    } else {
      settledAttribution = {
        navigationSource: completed.navigationSource,
        targetSection: completed.targetSection,
      };
      window.clearTimeout(attributionExpiryTimer);
      attributionExpiryTimer = window.setTimeout(() => {
        settledAttribution = null;
        attributionExpiryTimer = 0;
        dispatchTrackingState();
      }, ARRIVAL_ATTRIBUTION_MS);
    }
    dispatchTrackingState();
  };

  const scheduleNavigationSettle = (delay = 180) => {
    window.clearTimeout(navigationSettleTimer);
    navigationSettleTimer = window.setTimeout(finishNavigation, delay);
  };

  const stopNavigationAnimation = () => {
    if (navigationAnimationFrame) window.cancelAnimationFrame(navigationAnimationFrame);
    navigationAnimationFrame = 0;
    if (navigationScrollBehavior !== null) {
      document.documentElement.style.scrollBehavior = navigationScrollBehavior;
      navigationScrollBehavior = null;
    }
  };

  const cancelNavigation = () => {
    stopNavigationAnimation();
    if (!activeNavigation && !settledAttribution && !directEntryAttribution) return;
    window.clearTimeout(navigationSettleTimer);
    navigationSettleTimer = 0;
    activeNavigation = null;
    clearAttribution();
    dispatchTrackingState();
  };

  const animatedScrollTop = (target) => {
    const targetStyle = window.getComputedStyle(target);
    const rootStyle = window.getComputedStyle(document.documentElement);
    const scrollMarginTop = Number.parseFloat(targetStyle.scrollMarginTop) || 0;
    const scrollPaddingTop = Number.parseFloat(rootStyle.scrollPaddingTop) || 0;
    const documentHeight = document.documentElement.scrollHeight;
    const viewportHeight = window.innerHeight;
    return Math.max(0, Math.min(
      target.getBoundingClientRect().top + window.scrollY - scrollMarginTop - scrollPaddingTop,
      documentHeight - viewportHeight,
    ));
  };

  const animateNavigationScroll = (top, durationScale) => {
    stopNavigationAnimation();
    const from = window.scrollY;
    const distance = top - from;
    if (Math.abs(distance) < 1) {
      window.scrollTo(0, top);
      return 0;
    }
    const duration = Math.max(280, Math.min(1_000, Math.sqrt(Math.abs(distance)) * 20)) * durationScale;
    const startedAt = performance.now();
    navigationScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = "auto";
    const render = (now) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 4);
      window.scrollTo(0, from + distance * eased);
      if (progress < 1) {
        navigationAnimationFrame = window.requestAnimationFrame(render);
        return;
      }
      stopNavigationAnimation();
      scheduleNavigationSettle(80);
    };
    navigationAnimationFrame = window.requestAnimationFrame(render);
    return duration;
  };

  const startEagleLineNavigation = (target, navigationSource, options = {}) => {
    if (!target) return;
    window.clearTimeout(navigationSettleTimer);
    clearAttribution();
    activeNavigation = {
      arrivalMethod: "assisted",
      navigationSource,
      targetSection: contentSectionForElement(target),
    };
    dispatchTrackingState();
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const durationScale = Number.isFinite(options.durationScale) ? options.durationScale : 1;
    const hasExplicitTop = Number.isFinite(options.top);
    const requestedTop = hasExplicitTop || reduceMotion || durationScale !== 1
      ? hasExplicitTop ? options.top : animatedScrollTop(target)
      : null;
    if (reduceMotion) {
      const previousScrollBehavior = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = "auto";
      window.scrollTo(0, requestedTop);
      document.documentElement.style.scrollBehavior = previousScrollBehavior;
    } else if (durationScale !== 1) {
      animateNavigationScroll(requestedTop, durationScale);
      return;
    } else if (hasExplicitTop) {
      window.scrollTo({ top: options.top, behavior: reduceMotion ? "auto" : "smooth" });
    } else {
      target.scrollIntoView({
        behavior: reduceMotion ? "auto" : "smooth",
        block: "start",
        inline: "nearest",
      });
    }
    scheduleNavigationSettle(reduceMotion ? 80 : 240);
  };

  const normalizePath = (value) => {
    const trimmed = value.replace(/\/+$/, "");
    return trimmed || "/";
  };

  const hashTarget = (hash) => {
    if (typeof hash !== "string" || !hash.startsWith("#") || hash.length < 2) return null;
    try {
      return document.getElementById(decodeURIComponent(hash.slice(1)));
    } catch {
      return null;
    }
  };

  const sameDocumentAnchorTarget = (anchor) => {
    if (!anchor || anchor.hasAttribute("download")) return null;
    const targetName = anchor.getAttribute("target");
    if (targetName && targetName.toLowerCase() !== "_self") return null;
    let url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch {
      return null;
    }
    if (url.origin !== window.location.origin
      || normalizePath(url.pathname) !== normalizePath(window.location.pathname)
      || url.search !== window.location.search) return null;
    return hashTarget(url.hash);
  };

  const navigationSourceFor = (element) => {
    if (element?.closest?.("[data-purchase-cta]")) return "choose_product";
    if (element?.closest?.(".el-header, [data-island]")) return "header";
    if (element?.closest?.(".el-footer")) return "footer";
    return "content_link";
  };

  const sourceSectionFor = (element) => (
    contentSectionForElement(element)
    || dominantContentSection({ preferNavigationTarget: false })
    || "unknown"
  );

  const observeQualifiedVisibility = (element, delay, qualifies, callback) => {
    if (!element || !("IntersectionObserver" in window)) return () => {};
    let timer = 0;
    let qualifying = false;
    let latestEntry = null;
    let attempts = 0;
    const clearTimer = () => {
      window.clearTimeout(timer);
      timer = 0;
    };
    const cleanup = () => {
      clearTimer();
      observer.disconnect();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener(TRACKING_STATE_EVENT, handleTrackingState);
    };
    const schedule = () => {
      if (timer || !qualifying || activeNavigation || document.visibilityState !== "visible") return;
      timer = window.setTimeout(() => {
        timer = 0;
        if (!qualifying || activeNavigation || document.visibilityState !== "visible" || !element.isConnected) return;
        const completed = callback();
        if (completed !== false) {
          cleanup();
          return;
        }
        attempts += 1;
        if (attempts < 10) schedule();
        else cleanup();
      }, attempts ? 500 : delay);
    };
    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      latestEntry = entry || null;
      qualifying = Boolean(entry && qualifies(entry) && !activeNavigation);
      if (!qualifying) {
        clearTimer();
        return;
      }
      schedule();
    }, { threshold: [0, .01, .25, .5, .75] });
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        clearTimer();
        return;
      }
      qualifying = Boolean(latestEntry && qualifies(latestEntry) && !activeNavigation);
      schedule();
    };
    const handleTrackingState = () => {
      qualifying = Boolean(latestEntry && qualifies(latestEntry) && !activeNavigation);
      if (!qualifying) clearTimer();
      else schedule();
    };
    observer.observe(element);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener(TRACKING_STATE_EVENT, handleTrackingState);
    return cleanup;
  };

  const viewedCtaPlacements = new Set();
  document.querySelectorAll("[data-purchase-cta]").forEach((cta) => {
    const placement = cta.dataset.ctaPlacement;
    if (!placement) return;
    observeQualifiedVisibility(
      cta,
      500,
      (entry) => entry.isIntersecting && entry.intersectionRatio >= .5,
      () => {
        if (viewedCtaPlacements.has(placement)) return true;
        const attribution = currentViewAttribution();
        if (!attribution) return false;
        const published = publishEagleLineEvent("eagleline_cta_view", {
          placement,
          page_type: pageType,
          ...attribution,
        });
        if (published) viewedCtaPlacements.add(placement);
        return published;
      },
    );
  });

  let purchaseFormViewed = false;
  document.querySelectorAll("[data-product-form]").forEach((formRoot) => {
    const form = formRoot.querySelector("form") || formRoot;
    observeQualifiedVisibility(
      form,
      1000,
      (entry) => entry.isIntersecting && entry.intersectionRatio >= .5,
      () => {
        if (purchaseFormViewed) return true;
        const attribution = currentViewAttribution();
        if (!attribution) return false;
        const published = publishEagleLineEvent("eagleline_purchase_form_view", {
          page_type: pageType,
          ...attribution,
        });
        if (published) purchaseFormViewed = true;
        return published;
      },
    );
  });

  const viewedContentSections = new Set();
  let pendingContentSection = null;
  let pendingContentTimer = 0;
  let pendingContentAttempts = 0;
  let contentTrackingFrame = 0;
  const clearPendingContent = () => {
    window.clearTimeout(pendingContentTimer);
    pendingContentTimer = 0;
    pendingContentSection = null;
    pendingContentAttempts = 0;
  };
  const evaluateDominantContent = () => {
    contentTrackingFrame = 0;
    if (activeNavigation || document.visibilityState !== "visible") {
      clearPendingContent();
      return;
    }
    const sectionName = dominantContentSection();
    if (!sectionName || sectionName === "transition" || viewedContentSections.has(sectionName)) {
      clearPendingContent();
      return;
    }
    if (pendingContentSection !== sectionName) {
      clearPendingContent();
      pendingContentSection = sectionName;
    }
    if (pendingContentTimer) return;
    pendingContentTimer = window.setTimeout(() => {
      pendingContentTimer = 0;
      if (activeNavigation
        || document.visibilityState !== "visible"
        || dominantContentSection() !== pendingContentSection) {
        clearPendingContent();
        return;
      }
      const attribution = currentViewAttribution();
      if (!attribution) return;
      const published = publishEagleLineEvent("eagleline_content_view", {
        section_name: pendingContentSection,
        page_type: pageType,
        ...attribution,
      });
      if (published) {
        viewedContentSections.add(pendingContentSection);
        clearPendingContent();
        return;
      }
      pendingContentAttempts += 1;
      if (pendingContentAttempts < 10) evaluateDominantContent();
      else clearPendingContent();
    }, pendingContentAttempts ? 500 : CONTENT_STABILITY_MS);
  };
  const requestDominantContentEvaluation = () => {
    if (!contentTrackingFrame) contentTrackingFrame = window.requestAnimationFrame(evaluateDominantContent);
  };
  window.addEventListener("scroll", () => {
    if (activeNavigation) scheduleNavigationSettle();
    requestDominantContentEvaluation();
  }, { passive: true });
  window.addEventListener("resize", requestDominantContentEvaluation, { passive: true });
  window.addEventListener("scrollend", () => {
    if (activeNavigation && !navigationAnimationFrame) scheduleNavigationSettle(0);
  }, { passive: true });
  document.addEventListener("visibilitychange", requestDominantContentEvaluation);
  document.addEventListener(TRACKING_STATE_EVENT, requestDominantContentEvaluation);
  requestDominantContentEvaluation();

  const verticalNavigationKeys = new Set([
    "ArrowDown",
    "ArrowUp",
    "End",
    "Home",
    "PageDown",
    "PageUp",
    " ",
  ]);
  window.addEventListener("wheel", cancelNavigation, { passive: true });
  window.addEventListener("touchstart", cancelNavigation, { passive: true });
  window.addEventListener("keydown", (event) => {
    if (event.isTrusted && verticalNavigationKeys.has(event.key)) cancelNavigation();
  });

  document.addEventListener("click", (event) => {
    if ((!event.isTrusted && !analyticsPreview) || event.button !== 0) return;
    const purchaseCta = event.target.closest?.("[data-purchase-cta]");
    if (purchaseCta) {
      publishEagleLineEvent("eagleline_cta_click", {
        placement: purchaseCta.dataset.ctaPlacement,
        destination: purchaseCta.dataset.ctaDestination,
        page_type: pageType,
        source_section: sourceSectionFor(purchaseCta),
      });
    }

    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = event.target.closest?.("a[href]");
    if (!anchor || anchor.matches("[data-hl-dot]")) return;
    const target = sameDocumentAnchorTarget(anchor);
    if (!target) return;
    event.preventDefault();
    if (anchor.matches(".el-skip-link")) {
      target.scrollIntoView({ block: "start", inline: "nearest" });
      target.focus?.({ preventScroll: true });
      return;
    }
    const placement = anchor.dataset.ctaPlacement || "";
    const acceleratedPurchaseCta = anchor.matches("[data-purchase-cta]")
      && acceleratedPurchasePlacements.has(placement);
    startEagleLineNavigation(target, navigationSourceFor(anchor), {
      durationScale: acceleratedPurchaseCta ? .56 : 1,
    });
  });

  const initialHashTarget = hashTarget(window.location.hash);
  if (initialHashTarget) {
    activeNavigation = {
      arrivalMethod: "direct_entry",
      navigationSource: "direct_hash",
      targetSection: contentSectionForElement(initialHashTarget),
    };
    dispatchTrackingState();
    scheduleNavigationSettle(240);
  }
  window.addEventListener("hashchange", () => {
    const target = hashTarget(window.location.hash);
    window.clearTimeout(navigationSettleTimer);
    navigationSettleTimer = 0;
    activeNavigation = null;
    clearAttribution();
    if (target) {
      activeNavigation = {
        arrivalMethod: "assisted",
        navigationSource: "browser_history",
        targetSection: contentSectionForElement(target),
      };
      scheduleNavigationSettle(240);
    }
    dispatchTrackingState();
  });

  const openedFaqIds = new Set();
  document.querySelectorAll("details[data-faq-id]").forEach((details) => {
    const faqId = details.dataset.faqId;
    const summary = details.querySelector("summary");
    if (!faqId || !summary) return;
    summary.addEventListener("click", (event) => {
      if (!event.isTrusted || details.open || openedFaqIds.has(faqId)) return;
      window.requestAnimationFrame(() => {
        if (!details.open || openedFaqIds.has(faqId)) return;
        const published = publishEagleLineEvent("eagleline_faq_open", {
          faq_id: faqId,
          page_type: pageType,
        });
        if (published) openedFaqIds.add(faqId);
      });
    });
  });

  const selectionToastTimers = new WeakMap();
  const showSelectionGateToast = (selectionGate) => {
    const productForm = selectionGate.closest("[data-product-form]");
    const selectionToast = productForm?.querySelector("[data-selection-toast]");
    const prompt = selectionGate.getAttribute("aria-label") || "";
    if (!selectionToast || !prompt) return;
    const timers = selectionToastTimers.get(selectionToast) || {};
    window.clearTimeout(timers.hide);
    window.clearTimeout(timers.cleanup);
    selectionToast.classList.remove("is-visible");
    selectionToast.textContent = "";
    window.requestAnimationFrame(() => {
      selectionToast.textContent = prompt;
      selectionToast.setAttribute("aria-hidden", "false");
      selectionToast.classList.add("is-visible");
    });
    const hide = window.setTimeout(() => {
      selectionToast.classList.remove("is-visible");
      selectionToast.setAttribute("aria-hidden", "true");
      const cleanup = window.setTimeout(() => {
        selectionToast.textContent = "";
      }, window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 260);
      selectionToastTimers.set(selectionToast, { hide: 0, cleanup });
    }, 2600);
    selectionToastTimers.set(selectionToast, { hide, cleanup: 0 });
  };

  document.addEventListener("click", (event) => {
    const selectionGate = event.target.closest?.("[data-selection-gate]");
    if (selectionGate) {
      showSelectionGateToast(selectionGate);
      return;
    }
    if (!event.isTrusted) return;
    const checkoutControl = event.target.closest?.("[data-checkout-select]");
    if (!checkoutControl) return;
    if (checkoutControl.getAttribute("aria-disabled") === "true") return;
    const productForm = checkoutControl.closest("[data-product-form]");
    if (checkoutControl.matches("[data-direct-checkout]") && productForm?.dataset.selectionReady !== "true") return;
    const variantId = productForm?.querySelector("[data-variant-id]")?.value
      || checkoutControl.dataset.variantId;
    const parameters = {
      checkout_path: checkoutControl.dataset.checkoutPath || "direct_checkout",
      page_type: pageType,
    };
    if (variantId) parameters.variant_id = variantId;
    publishEagleLineEvent("eagleline_checkout_select", parameters);
  }, true);

  const initialize = () => {
  const motionPreference = window.matchMedia("(prefers-reduced-motion: reduce)");
  let reducedMotion = motionPreference.matches;
  const reducedData = Boolean(
    navigator.connection?.saveData
    || window.matchMedia?.("(prefers-reduced-data: reduce)").matches,
  );

  const onMediaChange = (query, callback) => {
    if (query.addEventListener) query.addEventListener("change", callback);
    else query.addListener?.(callback);
  };

  onMediaChange(motionPreference, (event) => {
    reducedMotion = event.matches;
    if (reducedMotion) document.querySelectorAll("video").forEach((video) => video.pause());
    document.dispatchEvent(new CustomEvent("eagleline:media-state"));
  });

  const isVisible = (element) => {
    if (!element || element.hidden || element.getAttribute("aria-hidden") === "true") return false;
    if (!element.getClientRects().length) return false;
    const style = window.getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden";
  };

  const observeVisibility = (element, callback, options = {}) => {
    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        ([entry]) => callback(entry.isIntersecting, entry.intersectionRatio),
        options,
      );
      observer.observe(element);
      return () => observer.disconnect();
    }

    let frame = 0;
    const check = () => {
      frame = 0;
      const rect = element.getBoundingClientRect();
      const intersects = rect.bottom > 0 && rect.top < window.innerHeight;
      callback(intersects, intersects ? 1 : 0);
    };
    const requestCheck = () => {
      if (!frame) frame = window.requestAnimationFrame(check);
    };
    window.addEventListener("scroll", requestCheck, { passive: true });
    window.addEventListener("resize", requestCheck, { passive: true });
    check();
    return () => {
      window.removeEventListener("scroll", requestCheck);
      window.removeEventListener("resize", requestCheck);
      if (frame) window.cancelAnimationFrame(frame);
    };
  };

  const safePlay = (video) => {
    if (!(video instanceof HTMLVideoElement)) return;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.play().catch(() => {});
  };

  document.querySelectorAll("[data-header]").forEach((header) => {
    let scrolled = null;
    const update = () => {
      const nextScrolled = window.scrollY > 12;
      if (nextScrolled === scrolled) return;
      scrolled = nextScrolled;
      header.classList.toggle("is-scrolled", scrolled);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
  });


  document.querySelectorAll("[data-hero-motion]").forEach((hero) => {
    let pointerX = 0;
    let pointerY = 0;
    let currentX = 0;
    let currentY = 0;
    let scrollY = 0;
    let frame = 0;

    const render = () => {
      frame = 0;
      if (reducedMotion) {
        currentX = 0;
        currentY = 0;
        hero.style.setProperty("--hero-x", "0px");
        hero.style.setProperty("--hero-y", "0px");
        return;
      }
      const goalY = pointerY + scrollY;
      currentX += (pointerX - currentX) * 0.14;
      currentY += (goalY - currentY) * 0.14;
      hero.style.setProperty("--hero-x", `${currentX.toFixed(2)}px`);
      hero.style.setProperty("--hero-y", `${currentY.toFixed(2)}px`);
      if (Math.abs(pointerX - currentX) > 0.04 || Math.abs(goalY - currentY) > 0.04) {
        frame = window.requestAnimationFrame(render);
      }
    };

    const requestRender = () => {
      if (!frame) frame = window.requestAnimationFrame(render);
    };

    const updateScroll = () => {
      if (reducedMotion) return requestRender();
      const rect = hero.getBoundingClientRect();
      const viewportProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
      scrollY = (Math.max(0, Math.min(1, viewportProgress)) - 0.5) * 10;
      /* Scroll-out follow-up to the load swipe. Two drivers, set on the root
         so the hero, the press baton line and the spine track all read them:
         --hero-exit-edge starts at first scroll and sweeps the twin cobalt
         bars back across the pinned hero in the reverse direction of the
         intro, while --hero-exit waits out a 10% hero-height dead zone
         before the copy sinks/dissolves and the photo settles and dims.
         The edge handoff finishes later than the copy exit so its cobalt
         baton carries deliberately into the proof chapter. */
      const exit = Math.max(0, Math.min(1, (window.scrollY - rect.height * 0.1) / (rect.height * 0.28)));
      document.documentElement.style.setProperty("--hero-exit", exit.toFixed(3));
      const edgeExit = Math.max(0, Math.min(1, window.scrollY / (rect.height * 0.46)));
      document.documentElement.style.setProperty("--hero-exit-edge", edgeExit.toFixed(3));
      requestRender();
    };

    hero.addEventListener("pointermove", (event) => {
      if (reducedMotion || event.pointerType === "touch") return;
      const rect = hero.getBoundingClientRect();
      pointerX = ((event.clientX - rect.left) / rect.width - 0.5) * 10;
      pointerY = ((event.clientY - rect.top) / rect.height - 0.5) * 8;
      requestRender();
    });
    hero.addEventListener("pointerleave", () => {
      pointerX = 0;
      pointerY = 0;
      requestRender();
    });
    window.addEventListener("scroll", updateScroll, { passive: true });
    window.addEventListener("resize", updateScroll, { passive: true });
    onMediaChange(motionPreference, updateScroll);
    updateScroll();
  });

  /* Bar-linked intro: instead of a fixed time stagger, each copy element
     reveals (rise + fade) at the moment the intro swipe bars physically
     cross it. The bars literally write the copy in. Without JS or under
     reduced motion the CSS time-staggered entrance runs unchanged. */
  document.querySelectorAll(".el-v63-hero-full").forEach((hero) => {
    const edge = hero.querySelector(".el-hero-edge");
    const copy = hero.querySelector(".el-v6-hero-copy, .el-v63-hero-copy");
    /* The bar-linked version reads live geometry each animation frame. The
       compact layout keeps the same entrance via the CSS stagger instead,
       avoiding forced-layout work on phones and portrait tablets. */
    const compactViewport = window.matchMedia("(max-width: 920px)").matches;
    if (!edge || !copy || reducedMotion || compactViewport) return;
    const items = [...copy.children];
    if (!items.length) return;
    hero.classList.add("bar-linked");
    const scale = Math.min(1, window.innerWidth / 1440);
    const offsets = [40, 170, 300, 420, 510, 590].map((v) => v * scale);
    const started = performance.now();
    const tick = (now) => {
      const barX = edge.getBoundingClientRect().right;
      let pending = false;
      items.forEach((el, index) => {
        if (el.classList.contains("is-in")) return;
        const trigger = el.getBoundingClientRect().left + (offsets[index] || offsets[offsets.length - 1]);
        if (barX >= trigger || now - started > 2600) {
          el.classList.add("is-in");
        } else {
          pending = true;
        }
      });
      if (pending && now - started < 2800) window.requestAnimationFrame(tick);
    };
    window.requestAnimationFrame(tick);
  });

  /* Shared carousel: dot anchors remain useful without JS; enhanced controls
     move one centered card at a time and keep the active state synchronized. */
  document.querySelectorAll("[data-highlights]").forEach((section) => {
    const track = section.querySelector("[data-hl-track]");
    const slides = [...section.querySelectorAll("[data-hl-slide]")];
    const dots = [...section.querySelectorAll("[data-hl-dot]")];
    let previous = null;
    let next = null;
    if (!track || !slides.length) return;
    if (!reducedMotion) track.classList.add("is-live");
    const timerText = section.querySelector(".el-v63-hl-symbol text");
    let timerFrame = 0;
    let timerWasActive = false;
    let frame = 0;
    let currentIndex = -1;
    let manualStartIndex = -1;
    let manualIntentTimer = 0;
    let pointerStart = null;
    let ignoreSlideClickUntil = 0;
    const clearManualIntent = () => {
      window.clearTimeout(manualIntentTimer);
      manualIntentTimer = 0;
      manualStartIndex = -1;
    };
    const syncControls = (index) => {
      [[previous, index <= 0], [next, index >= slides.length - 1]].forEach(([control, disabled]) => {
        if (!control) return;
        control.disabled = disabled;
        control.setAttribute("aria-disabled", String(disabled));
      });
    };
    const update = () => {
      frame = 0;
      /* Cards are narrower than the track, so the active slide is the one
         whose center is closest to the track's visible center. */
      const center = track.getBoundingClientRect().left + track.clientWidth / 2;
      let index = 0;
      let best = Infinity;
      slides.forEach((slide, i) => {
        const rect = slide.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - center);
        if (distance < best) { best = distance; index = i; }
      });
      dots.forEach((dot, i) => {
        const active = i === index;
        dot.classList.toggle("is-active", active);
        if (active) dot.setAttribute("aria-current", "true");
        else dot.removeAttribute("aria-current");
      });
      slides.forEach((slide, i) => slide.classList.toggle("is-active", i === index));
      syncControls(index);
      if (manualStartIndex >= 0 && index !== manualStartIndex) {
        publishEagleLineEvent("eagleline_media_engage", {
          media_type: "image",
          placement: "mid_page",
          action: "gallery_select",
        });
        clearManualIntent();
      }
      currentIndex = index;
      /* Slide 3's timer counts up to 00:05 like a stopwatch each time its
         card centers; the arc sweep is CSS on the same activation. */
      const timerActive = Boolean(slides[index] && slides[index].matches(".el-v63-hl-icon.is-active"));
      if (timerText && !reducedMotion) {
        if (timerActive && !timerWasActive) {
          window.cancelAnimationFrame(timerFrame);
          const started = performance.now();
          const tick = (now) => {
            const t = Math.max(0, Math.min(1, (now - started - 150) / 1200));
            timerText.textContent = "00:0" + Math.min(5, Math.ceil(t * 5));
            if (t < 1) timerFrame = window.requestAnimationFrame(tick);
          };
          timerFrame = window.requestAnimationFrame(tick);
        } else if (!timerActive && timerWasActive) {
          window.cancelAnimationFrame(timerFrame);
          timerText.textContent = "00:05";
        }
      }
      timerWasActive = timerActive;
    };
    const request = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    const markManualIntent = (event) => {
      if (!event.isTrusted) return;
      manualStartIndex = currentIndex;
      window.clearTimeout(manualIntentTimer);
      manualIntentTimer = window.setTimeout(clearManualIntent, 2000);
    };
    const scrollToIndex = (index, event) => {
      const targetIndex = Math.max(0, Math.min(slides.length - 1, index));
      if (targetIndex === currentIndex) return;
      const target = slides[targetIndex];
      const trackRect = track.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      markManualIntent(event);
      track.scrollTo({
        left: track.scrollLeft + targetRect.left + targetRect.width / 2
          - trackRect.left - track.clientWidth / 2,
        behavior: reducedMotion ? "auto" : "smooth",
      });
    };
    const beginPointer = (event) => {
      markManualIntent(event);
      if (!event.isPrimary || (event.pointerType === "mouse" && event.button !== 0)) return;
      pointerStart = {
        id: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        scrollLeft: track.scrollLeft,
        moved: false,
      };
    };
    const trackPointer = (event) => {
      if (!pointerStart || event.pointerId !== pointerStart.id) return;
      if (Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y) > 8) {
        pointerStart.moved = true;
      }
    };
    const finishPointer = (event) => {
      if (!pointerStart || event.pointerId !== pointerStart.id) return;
      if (pointerStart.moved || Math.abs(track.scrollLeft - pointerStart.scrollLeft) > 4) {
        ignoreSlideClickUntil = Date.now() + 350;
      }
      pointerStart = null;
    };
    track.addEventListener("pointerdown", beginPointer, { passive: true });
    track.addEventListener("pointermove", trackPointer, { passive: true });
    track.addEventListener("pointerup", finishPointer, { passive: true });
    track.addEventListener("pointercancel", finishPointer, { passive: true });
    track.addEventListener("wheel", markManualIntent, { passive: true });
    track.addEventListener("keydown", (event) => {
      if (["ArrowLeft", "ArrowRight", "Home", "End", "PageUp", "PageDown"].includes(event.key)) {
        markManualIntent(event);
      }
    });
    previous = section.querySelector("[data-carousel-prev]");
    next = section.querySelector("[data-carousel-next]");
    previous?.addEventListener("click", (event) => scrollToIndex(currentIndex - 1, event));
    next?.addEventListener("click", (event) => scrollToIndex(currentIndex + 1, event));
    dots.forEach((dot, index) => dot.addEventListener("click", (event) => {
      event.preventDefault();
      scrollToIndex(index, event);
    }));
    slides.forEach((slide, index) => slide.addEventListener("click", (event) => {
      if (Date.now() < ignoreSlideClickUntil || index === currentIndex) return;
      const trackRect = track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      if (Math.min(trackRect.right, slideRect.right) <= Math.max(trackRect.left, slideRect.left)) return;
      scrollToIndex(currentIndex + (index < currentIndex ? -1 : 1), event);
    }));
    track.addEventListener("scroll", request, { passive: true });
    window.addEventListener("resize", request, { passive: true });
    update();
  });

  /* Universal text reveal: every text element outside the chrome and the
     zones with their own choreography rises in once as it enters the
     viewport and never hides again. Leaf-level text only (a block whose
     descendants carry text reveals as one unit). Armed state is applied by
     JS only when motion is allowed; otherwise everything is simply visible. */
  if (!reducedMotion) {
    const revealZones = document.querySelectorAll("main, .el-footer");
    const revealExclude = ".el-v63-hero, .el-v63-hl-track, .el-v63-patent-gallery, .el-v63-five, .el-v63-diff-rows, .el-spine, .el-island, .el-header";
    const revealSelector = ".el-kicker, h1, h2, h3, h4, h5, p, li, figcaption, summary, dt, dd";
    const revealItems = [];
    revealZones.forEach((zone) => {
      zone.querySelectorAll(revealSelector).forEach((el) => {
        if (el.closest(revealExclude)) return;
        if (el.querySelector(revealSelector)) return; // keep reveals leaf-level
        if (el.closest("details:not([open])")) return; // collapsed content stays ready
        if (el.offsetParent === null) return; // hidden until shown by other logic
        revealItems.push(el);
      });
    });
    const revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-in");
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0 });
    const revealSiblings = new Map();
    revealItems.forEach((el) => {
      const parent = el.parentElement;
      const index = revealSiblings.get(parent) || 0;
      revealSiblings.set(parent, index + 1);
      el.style.setProperty("--reveal-delay", `${Math.min(index * 70, 350)}ms`);
      el.classList.add("el-reveal");
      revealObserver.observe(el);
    });
  }

  /* Hide the floating header while the purchase chapter itself is on screen,
     then restore it when the customer scrolls above or below that chapter. */
  document.querySelectorAll("[data-island]").forEach((island) => {
    const heroAnchor = document.querySelector(".el-hero-pin") || document.querySelector(".el-v63-hero");
    const header = document.querySelector("[data-header]");
    const productGallery = document.querySelector("[data-product-gallery]")
      || document.querySelector("#eagleline-buy");
    const productRegion = productGallery?.closest("section") || productGallery;
    let visible = false;
    let frame = 0;
    const setVisible = (nextVisible) => {
      if (nextVisible === visible) return;
      visible = nextVisible;
      island.classList.toggle("is-visible", visible);
      island.setAttribute("aria-hidden", String(!visible));
      island.toggleAttribute("inert", !visible);
    };
    const update = () => {
      frame = 0;
      const vh = window.innerHeight;
      const productRect = productRegion?.getBoundingClientRect();
      const productInView = Boolean(productRect && productRect.top < vh && productRect.bottom > 0);
      const openingCleared = heroAnchor
        ? heroAnchor.getBoundingClientRect().bottom <= vh * (visible ? .16 : .1)
        : pageType === "product" && ((header?.getBoundingClientRect().bottom || 0) <= 0 || window.scrollY > 96);
      setVisible(openingCleared && !productInView);
    };
    const request = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", request, { passive: true });
    window.addEventListener("resize", request, { passive: true });
    update();
  });

  document.querySelectorAll("[data-spine]").forEach((spine) => {
    const progressEl = spine.querySelector(".el-spine-progress");
    const tipEl = spine.querySelector(".el-spine-tip");
    const labels = {
      top: themeString("spine_top", "Top"),
      "why-line": themeString("spine_why", "Why a line helps"),
      ritual: themeString("spine_ritual", "How to use"),
      story: themeString("spine_story", "Story"),
      technology: themeString("spine_technology", "Mechanism"),
      makuake: themeString("spine_makuake", "Makuake history"),
      "eagleline-buy": themeString("spine_buy", "Choose a product"),
    };
    /* Interstitial moments get no spine node: the highlights carousel, the
       two posters and the breather are transitions, not chapters. The FAQ
       is folded into the buy node, which carries the page's tail end. */
    const spineSkip = new Set(["highlights", "poster-line", "form", "poster-score", "faq"]);
    const chapters = [...document.querySelectorAll("main section[id]")].filter((section) => !spineSkip.has(section.id));
    const count = chapters.length;
    /* Where the spine dot conceptually "is" for a chapter: the section top
       sits just under the dynamic island when it is showing, otherwise with
       a small gap — measured live so framed sections never sit under chrome.
       Also precompute which chapters sit on dark or cobalt backgrounds so
       the spine can switch to its light scheme there. */
    const focusOffset = () => {
      const island = document.querySelector("[data-island]");
      if (island && island.classList.contains("is-visible")) {
        return island.getBoundingClientRect().bottom + 16;
      }
      return 24;
    };
    const chapterIsDark = chapters.map((chapter) => {
      const bg = getComputedStyle(chapter).backgroundColor.match(/[\d.]+/g);
      if (!bg || bg.length < 3 || (bg.length > 3 && parseFloat(bg[3]) === 0)) return false;
      const luminance = (0.2126 * bg[0] + 0.7152 * bg[1] + 0.0722 * bg[2]) / 255;
      return luminance < 0.45;
    });
    const nodes = chapters.map((chapter) => {
      const text = labels[chapter.id] || chapter.id;
      const node = document.createElement("button");
      node.type = "button";
      node.className = "el-spine-node";
      node.setAttribute("aria-label", themeString("spine_go_to", "Go to __SECTION__").replace("__SECTION__", text));
      if (chapter.id === "eagleline-buy") {
        node.dataset.purchaseCta = "";
        node.dataset.ctaPlacement = "section_nav";
        node.dataset.ctaDestination = "product_gallery";
      }
      const label = document.createElement("span");
      label.className = "el-spine-label";
      label.setAttribute("aria-hidden", "true");
      label.textContent = text;
      node.appendChild(label);
      node.addEventListener("click", () => {
        const top = chapter.getBoundingClientRect().top + window.scrollY;
        const target = Math.max(0, top - focusOffset());
        startEagleLineNavigation(chapter, "spine", { top: target });
      });
      spine.appendChild(node);
      return node;
    });

    let idleTimer = 0;
    const armIdle = () => {
      window.clearTimeout(idleTimer);
      spine.classList.remove("labels-idle");
      idleTimer = window.setTimeout(() => spine.classList.add("labels-idle"), 1200);
    };
    const setExpanded = (on) => {
      spine.classList.toggle("is-expanded", on);
      if (on) {
        window.clearTimeout(idleTimer);
        spine.classList.remove("labels-idle");
      } else {
        armIdle();
      }
    };
    window.addEventListener("pointermove", (event) => {
      if (event.pointerType === "touch") return;
      const near = event.clientX <= 96;
      if (near !== spine.classList.contains("is-expanded")) setExpanded(near);
    }, { passive: true });

    let frame = 0;
    let lastActive = -1;
    const update = () => {
      frame = 0;
      const scrollMax = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const p = Math.min(1, Math.max(0, window.scrollY / scrollMax));
      /* Every chapter gets its natural anchor (its framed scroll position),
         including the last one: forcing the final anchor to 1 desynced the
         tip from the final sections' dots. The tip rests on the last node
         from that section's framed position to the page bottom. */
      const anchors = chapters.map((chapter, index) => {
        if (index === 0) return 0;
        const top = chapter.getBoundingClientRect().top + window.scrollY;
        return Math.min(1, Math.max(0, (top - focusOffset()) / scrollMax));
      });
      let segment = 0;
      for (let index = 0; index < count - 1; index += 1) {
        if (p >= anchors[index]) segment = index;
      }
      segment = Math.min(segment, count - 2);
      const a0 = anchors[segment];
      const a1 = anchors[segment + 1];
      const local = a1 > a0 ? Math.min(1, Math.max(0, (p - a0) / (a1 - a0))) : 1;
      const spinePos = Math.min(1, count > 1 ? (segment + local) / (count - 1) : 0);
      const activeIndex = Math.round(spinePos * (count - 1));
      if (activeIndex !== lastActive) {
        lastActive = activeIndex;
        spine.classList.toggle("is-inverted", chapterIsDark[activeIndex] === true);
        if (!spine.classList.contains("is-expanded")) armIdle();
      }
      if (progressEl) progressEl.style.height = `${spinePos * 100}%`;
      if (tipEl) tipEl.style.top = `${spinePos * 100}%`;
      nodes.forEach((node, index) => {
        const pos = count > 1 ? index / (count - 1) : 0;
        node.style.top = `${pos * 100}%`;
        node.classList.toggle("is-passed", spinePos >= pos - 0.001);
        node.classList.toggle("is-active", index === activeIndex);
      });
    };
    const requestUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate, { passive: true });
    update();
  });

  /* Five-second steps: the three timestamped steps light in sequence with
     scroll. Without JS or under reduced motion they stay fully lit (CSS
     default); JS only "arms" the dim state when animation is allowed. */
  document.querySelectorAll("[data-five]").forEach((block) => {
    const stage = block.querySelector(".el-v63-five-stage");
    const steps = [...block.querySelectorAll(".el-v63-five-steps li")];
    if (!stage || reducedMotion) return;
    const thresholds = [0.15, 0.5, 0.85];
    block.classList.add("is-armed");
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = stage.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.max(0, Math.min(1, (vh * 0.85 - rect.top) / (vh * 0.5)));
      steps.forEach((li, index) => li.classList.toggle("is-lit", p >= thresholds[index]));
    };
    const request = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", request, { passive: true });
    window.addEventListener("resize", request, { passive: true });
    update();
  });

  /* Angle diagram entry draw: the four angle lines draw themselves once when
     the diagram scrolls into view. Path lengths are measured so the dash
     animation needs no magic numbers; the overlay then fades back to the
     baked artwork. Skipped under reduced motion (image lines suffice). */
  document.querySelectorAll(".el-v63-angle-media").forEach((media) => {
    const draw = media.querySelector(".el-v63-angle-draw");
    if (!draw || reducedMotion) return;
    draw.querySelectorAll("path").forEach((path) => {
      const length = path.getTotalLength();
      path.style.setProperty("--len", `${length}px`);
      path.style.strokeDasharray = `${length}`;
    });
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        media.classList.add("is-drawn");
        observer.disconnect();
      });
    }, { threshold: 0.35 });
    observer.observe(media);
  });

  /* Ball lane: a dimpled golf ball rolls the full page width left-to-right
     with scroll, rotating clockwise and drawing a cobalt line behind it —
     the ball marks the line. When the roll completes, the line flashes.
     Static ball, no mark, under reduced motion / no-JS. */
  document.querySelectorAll("[data-ball-lane]").forEach((lane) => {
    const ball = lane.querySelector(".el-v63-ball-lane-ball");
    const mark = lane.querySelector(".el-v63-ball-lane-mark");
    if (!ball || !mark || reducedMotion) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = lane.getBoundingClientRect();
      const vh = window.innerHeight;
      const p = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)));
      const startX = window.innerWidth - 48;
      const x = 4 + p * (startX - 4);
      const rotation = ((x - 4) / 69.1) * 360;
      ball.style.transform = `translate(${x.toFixed(1)}px, -50%) rotate(${rotation.toFixed(1)}deg)`;
      mark.style.width = `${(x + 11).toFixed(1)}px`;
      lane.classList.toggle("is-complete", p >= 0.985);
    };
    const request = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    window.addEventListener("scroll", request, { passive: true });
    window.addEventListener("resize", request, { passive: true });
    update();
  });

  /* Versus rows: staggered entry choreography. Each row rises in as it
     enters, the pain photo settles into its muted grade, the answer photo
     lifts, and the cobalt edge draws down beside the answer title. Final
     state is the CSS default; JS only arms the hidden start. */
  document.querySelectorAll(".el-v63-diff-rows").forEach((rows) => {
    if (reducedMotion) return;
    const items = [...rows.querySelectorAll(".el-v63-diff-row")];
    if (!items.length) return;
    rows.classList.add("is-armed");
    items.forEach((row, index) => { row.style.transitionDelay = `${index * 90}ms`; });
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-in");
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.3 });
    items.forEach((row) => observer.observe(row));
  });

  document.querySelectorAll("[data-reveal]").forEach((element) => {
    if (reducedMotion) {
      element.classList.add("is-visible");
      return;
    }
    let stopObserving = () => {};
    stopObserving = observeVisibility(
      element,
      (inView) => {
        if (!inView) return;
        element.classList.add("is-visible");
        stopObserving();
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.15 },
    );
  });

  document.querySelectorAll("video[data-viewport-video]").forEach((video) => {
    if (video.closest("[data-mechanism-switcher]")) return;
    let inView = false;
    let playbackEnabled = !reducedMotion && !reducedData;
    let lastReducedMotion = reducedMotion;
    let nativeControlIntent = false;
    let nativeControlIntentTimer = 0;

    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.pause();

    const playbackRequested = () => playbackEnabled && !reducedMotion;
    const sync = () => {
      if (playbackRequested() && !document.hidden && inView && isVisible(video)) safePlay(video);
      else video.pause();
    };
    const markNativeControlIntent = () => {
      nativeControlIntent = true;
      window.clearTimeout(nativeControlIntentTimer);
      nativeControlIntentTimer = window.setTimeout(() => { nativeControlIntent = false; }, 800);
    };
    video.addEventListener("pointerdown", markNativeControlIntent, { passive: true });
    video.addEventListener("keydown", markNativeControlIntent);
    video.addEventListener("play", () => {
      if (nativeControlIntent) playbackEnabled = true;
    });
    video.addEventListener("pause", () => {
      if (nativeControlIntent) playbackEnabled = false;
    });
    observeVisibility(
      video,
      (visible) => {
        inView = visible;
        sync();
      },
      { threshold: 0.28 },
    );
    document.addEventListener("visibilitychange", sync);
    document.addEventListener("eagleline:media-state", () => {
      if (reducedMotion !== lastReducedMotion) {
        lastReducedMotion = reducedMotion;
        playbackEnabled = !reducedMotion && !reducedData;
      }
      sync();
    });
  });

  document.querySelectorAll("[data-youtube-embed]").forEach((embed) => {
    const button = embed.querySelector("[data-youtube-play]");
    const videoId = embed.dataset.youtubeId;
    if (!button || !videoId) return;

    button.addEventListener("click", (event) => {
      if (event.isTrusted) {
        publishEagleLineEvent("eagleline_media_engage", {
          media_type: "video",
          placement: "mid_page",
          action: "video_play",
        });
      }
      const iframe = document.createElement("iframe");
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
      iframe.title = themeString("lesson_video_title", "Kina Golf explains how to use a line on a golf ball when putting");
      iframe.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
      iframe.referrerPolicy = "strict-origin-when-cross-origin";
      iframe.allowFullscreen = true;
      iframe.tabIndex = 0;
      button.replaceWith(iframe);
      iframe.focus();
    });
  });

  const gallerySequences = new WeakMap();
  const galleryImageLoads = new Map();
  const GALLERY_MAIN_SIZES = "(max-width: 820px) min(calc(100vw - 32px), 680px), (max-width: 1100px) and (orientation: portrait) min(calc(100vw - 48px), 920px), (max-width: 1100px) 48vw, 640px";
  const galleryControlSources = (control) => {
    const fallback = control?.dataset.galleryThumb || "";
    const midSrc = control?.dataset.galleryMidSrc || "";
    const fullSrc = control?.dataset.galleryFullSrc || fallback;
    const declaredFullWidth = Number(control?.dataset.galleryWidth);
    const fullWidth = Number.isInteger(declaredFullWidth) && declaredFullWidth > 0
      ? declaredFullWidth
      : 1200;
    const src = fullSrc || midSrc || fallback;
    const srcset = midSrc && fullSrc ? `${midSrc} 768w, ${fullSrc} ${fullWidth}w` : "";
    return {
      src,
      srcset,
      sizes: srcset ? GALLERY_MAIN_SIZES : "",
    };
  };

  const loadGalleryImage = (media) => {
    const imageMedia = typeof media === "string" ? { src: media } : media;
    const source = imageMedia?.src;
    if (!source) return Promise.reject(new Error("Missing gallery image source"));
    const srcset = imageMedia.srcset || "";
    const sizes = imageMedia.sizes || "";
    const responsiveContext = srcset
      ? `${window.innerWidth || 0}@${window.devicePixelRatio || 1}`
      : "";
    const cacheKey = JSON.stringify([source, srcset, sizes, responsiveContext]);
    const cached = galleryImageLoads.get(cacheKey);
    if (cached) return cached;

    const request = new Promise((resolve, reject) => {
      const preload = new Image();
      let settled = false;
      const timeout = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error("Gallery image load timed out"));
      }, 8000);
      const finish = async () => {
        if (settled) return;
        try {
          await preload.decode?.();
        } catch {}
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        resolve(preload);
      };
      preload.onload = finish;
      preload.onerror = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeout);
        reject(new Error("Gallery image failed to load"));
      };
      preload.decoding = "async";
      if (srcset) {
        preload.sizes = sizes || GALLERY_MAIN_SIZES;
        preload.srcset = srcset;
      }
      preload.src = source;
      if (preload.complete && preload.naturalWidth) window.queueMicrotask(finish);
    });

    galleryImageLoads.set(cacheKey, request);
    request.catch(() => {
      if (galleryImageLoads.get(cacheKey) === request) galleryImageLoads.delete(cacheKey);
    });
    return request;
  };

  const warmGalleryControl = (control) => {
    if (reducedData || !control?.dataset.galleryThumb) return;
    loadGalleryImage(galleryControlSources(control)).catch(() => {});
  };

  const warmGalleryNeighbors = (gallery, activeControl = null) => {
    if (reducedData) return;
    const controls = [...gallery.querySelectorAll("[data-gallery-thumb]")];
    const current = activeControl
      || controls.find((control) => control.getAttribute("aria-pressed") === "true");
    const currentIndex = controls.indexOf(current);
    if (currentIndex < 0) return;
    const warm = () => {
      warmGalleryControl(controls[currentIndex - 1]);
      warmGalleryControl(controls[currentIndex + 1]);
    };
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(warm, { timeout: 1000 });
    } else {
      window.setTimeout(warm, 120);
    }
  };

  const galleryMeasuredInteractions = new WeakMap();

  const publishGalleryMediaEvent = (gallery, control, interactionSource, action = "gallery_select") => {
    const mediaId = control?.dataset.galleryMediaId;
    const mediaType = control?.dataset.galleryMediaType || "image";
    if (!mediaId || !interactionSource) return;
    const measured = galleryMeasuredInteractions.get(gallery) || new Set();
    const measurementKey = [mediaId, interactionSource, action].join(":");
    if (measured.has(measurementKey)) return;
    const published = publishEagleLineEvent("eagleline_media_engage", {
      media_type: mediaType,
      placement: pageType === "product" ? "product_page" : "purchase_section",
      action,
      media_id: mediaId,
      interaction_source: interactionSource,
    });
    if (!published) return;
    measured.add(measurementKey);
    galleryMeasuredInteractions.set(gallery, measured);
  };

  const setGalleryMedia = async (gallery, media, activeControl = null, options = {}) => {
    const frame = gallery.querySelector(".el-gallery-main");
    const mainImage = gallery.querySelector("[data-gallery-main]");
    const mainVideo = gallery.querySelector("[data-gallery-main-video]");
    const index = gallery.querySelector("[data-gallery-index]");
    const controls = [...gallery.querySelectorAll("[data-gallery-thumb]")];
    const mediaType = media?.mediaType === "video" ? "video" : "image";
    if (!frame || !mainImage || !mainVideo || !media?.src) return false;
    if (mediaType === "video" && !media.videoSrc) return false;
    const sequence = (gallerySequences.get(gallery) || 0) + 1;
    gallerySequences.set(gallery, sequence);

    const commit = () => {
      if (sequence !== gallerySequences.get(gallery)) return false;
      frame.dataset.galleryTreatment = media.treatment || "";

      if (mediaType === "video") {
        mainVideo.poster = media.src;
        mainVideo.setAttribute("aria-label", media.alt || themeString("gallery_fallback_alt", "EagleLine demonstration video"));
        if (mainVideo.dataset.gallerySource !== media.videoSrc) {
          mainVideo.pause();
          mainVideo.src = media.videoSrc;
          mainVideo.dataset.gallerySource = media.videoSrc;
          mainVideo.load();
        }
        mainImage.hidden = true;
        mainVideo.hidden = false;
        if (!reducedMotion && !reducedData && options.autoplay !== false) {
          window.setTimeout(() => {
            if (mainVideo.hidden) return;
            safePlay(mainVideo);
          }, 0);
        }
      } else {
        mainVideo.pause();
        mainVideo.hidden = true;
        mainImage.hidden = false;
        mainImage.sizes = media.sizes || GALLERY_MAIN_SIZES;
        mainImage.srcset = media.srcset || media.src;
        mainImage.src = media.src;
        mainImage.decoding = "async";
        mainImage.alt = media.alt || "EagleLine";
        if (media.width) mainImage.width = Number(media.width);
        if (media.height) mainImage.height = Number(media.height);
        mainImage.dataset.galleryFit = media.fit || "cover";
        mainImage.style.opacity = "1";
      }

      if (index && activeControl?.dataset.galleryIndexValue) {
        index.textContent = activeControl.dataset.galleryIndexValue;
      }
      controls.forEach((control) => {
        const active = control === activeControl;
        control.classList.toggle("is-active", active);
        control.setAttribute("aria-pressed", String(active));
      });

      const controlStrip = activeControl?.parentElement;
      if (controlStrip && controlStrip.scrollWidth > controlStrip.clientWidth) {
        window.requestAnimationFrame(() => {
          const left = activeControl.offsetLeft - (controlStrip.clientWidth - activeControl.offsetWidth) / 2;
          controlStrip.scrollTo({
            left: Math.max(0, left),
            behavior: options.instant || reducedMotion ? "auto" : "smooth",
          });
        });
      }
      document.dispatchEvent(new CustomEvent("eagleline:media-state"));
      warmGalleryNeighbors(gallery, activeControl);
      return true;
    };

    gallery.setAttribute("aria-busy", "true");
    try {
      await loadGalleryImage(media);
      return commit();
    } catch {
      return false;
    } finally {
      if (sequence === gallerySequences.get(gallery)) gallery.removeAttribute("aria-busy");
    }
  };

  const galleryControlMedia = (control) => ({
    ...galleryControlSources(control),
    videoSrc: control?.dataset.galleryVideoSrc,
    mediaType: control?.dataset.galleryMediaType || "image",
    mediaId: control?.dataset.galleryMediaId,
    alt: control?.dataset.galleryAlt,
    width: control?.dataset.galleryWidth,
    height: control?.dataset.galleryHeight,
    fit: control?.dataset.galleryFit,
    treatment: control?.dataset.galleryTreatment,
  });

  const selectGalleryControl = (gallery, control, event, options = {}) => {
    if (!control) return Promise.resolve(false);
    if (control.getAttribute("aria-pressed") === "true") {
      if (gallery.getAttribute("aria-busy") === "true") {
        gallerySequences.set(gallery, (gallerySequences.get(gallery) || 0) + 1);
        gallery.removeAttribute("aria-busy");
      }
      return Promise.resolve(false);
    }
    const customerSelection = Boolean(event?.isTrusted || options.customerSelection);
    const interactionSource = options.interactionSource || "thumbnail";
    return setGalleryMedia(gallery, galleryControlMedia(control), control, options).then((committed) => {
      if (committed && customerSelection) {
        publishGalleryMediaEvent(gallery, control, interactionSource);
      }
      return committed;
    });
  };
  const initializeGallerySwipe = (gallery, controls) => {
    const frame = gallery.querySelector(".el-gallery-main");
    const mainImage = gallery.querySelector("[data-gallery-main]");
    const mainVideo = gallery.querySelector("[data-gallery-main-video]");
    if (!frame || !mainImage || !mainVideo || !("PointerEvent" in window)) return;

    const lockDistance = 6;
    const flickDistance = 10;
    const velocityWindow = 120;
    const flickProjection = 220;
    const commitVelocity = .18;
    const quickGestureWindow = 180;
    const edgeResistance = .18;
    let gesture = null;
    let track = null;
    let settleTimer = 0;
    let settleComplete = null;

    mainImage.draggable = false;
    mainVideo.draggable = false;

    const trackImage = (control) => {
      const sourceImage = control?.querySelector("img");
      const media = galleryControlSources(control);
      const source = media.src || sourceImage?.currentSrc || sourceImage?.src;
      if (!source) return document.createElement("span");
      const image = document.createElement("img");
      if (media.srcset) {
        image.sizes = media.sizes || GALLERY_MAIN_SIZES;
        image.srcset = media.srcset;
      }
      image.src = source;
      image.decoding = "async";
      image.alt = "";
      image.draggable = false;
      image.setAttribute("aria-hidden", "true");
      image.dataset.galleryFit = control?.dataset.galleryFit || "cover";
      image.dataset.galleryTreatment = control?.dataset.galleryTreatment || "";
      return image;
    };

    const removeTrack = () => {
      window.clearTimeout(settleTimer);
      settleTimer = 0;
      if (track && settleComplete) track.removeEventListener("transitionend", settleComplete);
      settleComplete = null;
      frame.classList.remove("is-swipe-tracking", "is-swipe-settling");
      track?.remove();
      track = null;
    };

    const waitForMainMediaPaint = async () => {
      if (!mainImage.hidden && typeof mainImage.decode === "function") {
        const imageReady = new Promise((resolve) => {
          if (mainImage.complete && mainImage.naturalWidth) {
            resolve();
            return;
          }
          let timeout = 0;
          const finish = () => {
            window.clearTimeout(timeout);
            mainImage.removeEventListener("load", finish);
            mainImage.removeEventListener("error", finish);
            resolve();
          };
          mainImage.addEventListener("load", finish, { once: true });
          mainImage.addEventListener("error", finish, { once: true });
          timeout = window.setTimeout(finish, 800);
        });
        try {
          await Promise.race([mainImage.decode(), imageReady]);
        } catch {
          await imageReady;
        }
      }
      await new Promise((resolve) => {
        window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
      });
    };

    const buildTrack = (activeIndex) => {
      removeTrack();
      warmGalleryControl(controls[activeIndex - 1]);
      warmGalleryControl(controls[activeIndex + 1]);
      track = document.createElement("div");
      track.className = "el-gallery-swipe-track";
      track.setAttribute("aria-hidden", "true");
      track.append(
        activeIndex > 0 ? trackImage(controls[activeIndex - 1]) : document.createElement("span"),
        trackImage(controls[activeIndex]),
        activeIndex < controls.length - 1 ? trackImage(controls[activeIndex + 1]) : document.createElement("span"),
      );
      frame.append(track);
      frame.classList.add("is-swipe-tracking");
    };

    const resetGesture = () => {
      gesture = null;
      removeTrack();
    };

    const recordGestureSamples = (activeGesture, event) => {
      let events = [];
      try {
        events = event.getCoalescedEvents?.() || [];
      } catch {}
      [...events, event].forEach((sample) => {
        if (!Number.isFinite(sample.clientX) || !Number.isFinite(sample.timeStamp)) return;
        const last = activeGesture.samples[activeGesture.samples.length - 1];
        if (last && sample.timeStamp < last.time) return;
        if (last && sample.timeStamp === last.time) {
          last.x = sample.clientX;
          return;
        }
        activeGesture.samples.push({ x: sample.clientX, time: sample.timeStamp });
      });
      const cutoff = event.timeStamp - velocityWindow;
      activeGesture.samples = activeGesture.samples.filter((sample) => sample.time >= cutoff);
      const clock = performance.now();
      activeGesture.clockSamples.push({ x: event.clientX, time: clock });
      activeGesture.clockSamples = activeGesture.clockSamples.filter(
        (sample) => sample.time >= clock - velocityWindow,
      );
    };

    const gestureReleaseVelocity = (activeGesture, releaseClock) => {
      const samples = activeGesture.clockSamples.filter(
        (sample) => sample.time >= releaseClock - velocityWindow,
      );
      if (samples.length < 2) return 0;
      const first = samples[0];
      const last = samples[samples.length - 1];
      const elapsed = last.time - first.time;
      return elapsed > 0 ? (last.x - first.x) / elapsed : 0;
    };

    const finishGesture = (event, cancelled = false, useLastPoint = false) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const completedGesture = gesture;
      gesture = null;
      if (useLastPoint) {
        const clock = performance.now();
        completedGesture.clockSamples.push({ x: completedGesture.lastX, time: clock });
        completedGesture.clockSamples = completedGesture.clockSamples.filter(
          (sample) => sample.time >= clock - velocityWindow,
        );
      } else {
        recordGestureSamples(completedGesture, event);
      }
      const releaseX = !useLastPoint && Number.isFinite(event.clientX)
        ? event.clientX
        : completedGesture.lastX;
      const releaseY = !useLastPoint && Number.isFinite(event.clientY)
        ? event.clientY
        : completedGesture.lastY;
      completedGesture.deltaX = releaseX - completedGesture.startX;
      completedGesture.deltaY = releaseY - completedGesture.startY;
      if (frame.hasPointerCapture?.(completedGesture.pointerId)) {
        try {
          frame.releasePointerCapture(completedGesture.pointerId);
        } catch {}
      }
      if (cancelled || completedGesture.axis === "vertical") {
        removeTrack();
        return;
      }

      if (!completedGesture.axis) {
        const absoluteX = Math.abs(completedGesture.deltaX);
        const absoluteY = Math.abs(completedGesture.deltaY);
        if (
          Math.max(absoluteX, absoluteY) < lockDistance
          || absoluteX < lockDistance
          || absoluteY > absoluteX * 1.2
        ) {
          removeTrack();
          return;
        }
        completedGesture.axis = "horizontal";
        buildTrack(completedGesture.activeIndex);
      }

      if (!track) return;

      const width = frame.clientWidth || 1;
      const direction = completedGesture.deltaX < 0 ? 1 : -1;
      const targetIndex = completedGesture.activeIndex + direction;
      const hasTarget = targetIndex >= 0 && targetIndex < controls.length;
      const distance = Math.abs(completedGesture.deltaX);
      const distanceThreshold = Math.min(width * .16, 56);
      const releaseClock = performance.now();
      const recentVelocityX = gestureReleaseVelocity(completedGesture, releaseClock);
      const wholeGestureElapsed = releaseClock - completedGesture.startedAt;
      const wholeGestureVelocityX = wholeGestureElapsed > 0 && wholeGestureElapsed <= quickGestureWindow
        ? completedGesture.deltaX / wholeGestureElapsed
        : 0;
      const releaseVelocityX = Math.abs(wholeGestureVelocityX) > Math.abs(recentVelocityX)
        ? wholeGestureVelocityX
        : recentVelocityX;
      const projectedDistance = distance + Math.abs(releaseVelocityX) * flickProjection;
      const velocityCommits = distance >= flickDistance
        && Math.abs(releaseVelocityX) >= commitVelocity
        && Math.sign(releaseVelocityX) === Math.sign(completedGesture.deltaX)
        && projectedDistance >= distanceThreshold;
      const commits = hasTarget && (distance >= distanceThreshold || velocityCommits);
      const finalX = commits ? -direction * width : 0;
      const translatedX = hasTarget
        ? completedGesture.deltaX
        : completedGesture.deltaX * edgeResistance;
      track.style.transform = `translate3d(${translatedX}px, 0, 0)`;

      const complete = () => {
        if (commits) {
          selectGalleryControl(gallery, controls[targetIndex], event, {
            instant: true,
            interactionSource: "swipe",
          })
            .then(async (committed) => {
              if (committed) await waitForMainMediaPaint();
            })
            .finally(removeTrack);
          return;
        }
        removeTrack();
      };

      if (reducedMotion) {
        track.style.transform = `translate3d(${finalX}px, 0, 0)`;
        complete();
        return;
      }

      frame.classList.add("is-swipe-settling");
      const settlingTrack = track;
      let settled = false;
      const finishSettle = (transitionEvent = null) => {
        if (
          transitionEvent
          && (transitionEvent.target !== settlingTrack || transitionEvent.propertyName !== "transform")
        ) return;
        if (settled) return;
        settled = true;
        window.clearTimeout(settleTimer);
        settleTimer = 0;
        settlingTrack.removeEventListener("transitionend", finishSettle);
        if (settleComplete === finishSettle) settleComplete = null;
        complete();
      };
      settleComplete = finishSettle;
      settlingTrack.addEventListener("transitionend", finishSettle);
      window.requestAnimationFrame(() => {
        if (track !== settlingTrack) return;
        settlingTrack.style.transform = `translate3d(${finalX}px, 0, 0)`;
        settleTimer = window.setTimeout(finishSettle, 450);
      });
    };

    frame.addEventListener("pointerdown", (event) => {
      if (event.pointerType !== "touch") return;
      if (event.target.closest?.("[data-gallery-main-video]")) return;
      if (!event.isPrimary) {
        resetGesture();
        return;
      }
      if (event.button !== 0 || track || gallery.getAttribute("aria-busy") === "true") return;
      const activeIndex = controls.findIndex((control) => control.getAttribute("aria-pressed") === "true");
      if (activeIndex < 0) return;
      gesture = {
        pointerId: event.pointerId,
        activeIndex,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastY: event.clientY,
        deltaX: 0,
        deltaY: 0,
        samples: [{ x: event.clientX, time: event.timeStamp }],
        clockSamples: [{ x: event.clientX, time: performance.now() }],
        startedAt: performance.now(),
        axis: null,
      };
    });

    frame.addEventListener("pointermove", (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) return;
      const deltaX = event.clientX - gesture.startX;
      const deltaY = event.clientY - gesture.startY;
      gesture.lastX = event.clientX;
      gesture.lastY = event.clientY;
      const absoluteX = Math.abs(deltaX);
      const absoluteY = Math.abs(deltaY);

      if (!gesture.axis) {
        if (Math.max(absoluteX, absoluteY) < lockDistance) return;
        if (absoluteY >= 10 && absoluteY >= absoluteX * 1.73) {
          gesture.axis = "vertical";
          return;
        }
        if (absoluteX < lockDistance || absoluteY > absoluteX * 1.2) return;
        gesture.axis = "horizontal";
        buildTrack(gesture.activeIndex);
        try {
          frame.setPointerCapture(event.pointerId);
        } catch {}
      }

      if (gesture.axis !== "horizontal" || !track) return;
      event.preventDefault();
      recordGestureSamples(gesture, event);
      gesture.deltaX = deltaX;
      gesture.deltaY = deltaY;
      const direction = deltaX < 0 ? 1 : -1;
      const targetIndex = gesture.activeIndex + direction;
      const hasTarget = targetIndex >= 0 && targetIndex < controls.length;
      const translatedX = hasTarget ? deltaX : deltaX * edgeResistance;
      track.style.transform = `translate3d(${translatedX}px, 0, 0)`;
    }, { passive: false });

    frame.addEventListener("pointerup", (event) => finishGesture(event));
    frame.addEventListener("pointercancel", (event) => {
      finishGesture(event, gesture?.axis !== "horizontal", true);
    });
    frame.addEventListener("lostpointercapture", (event) => {
      if (event.target !== frame) return;
      if (gesture && event.pointerId === gesture.pointerId) resetGesture();
    });
  };

  const initializeGalleryRailHint = (gallery) => {
    const rail = gallery.querySelector(".el-gallery-controls");
    const coarsePointer = window.matchMedia?.("(pointer: coarse)").matches;
    if (!rail || !coarsePointer || reducedMotion || reducedData || !("IntersectionObserver" in window)) return;

    let cancelled = false;
    let animation = null;
    const cancel = () => {
      cancelled = true;
      animation?.cancel?.();
      observer.disconnect();
      rail.style.removeProperty("scroll-snap-type");
    };
    ["pointerdown", "wheel", "keydown", "touchstart"].forEach((eventName) => {
      gallery.addEventListener(eventName, cancel, { once: true, passive: eventName !== "keydown" });
    });
    gallery.addEventListener("eagleline:variant-change", (event) => {
      if (event.detail?.userInitiated) cancel();
    });

    const observer = new IntersectionObserver((entries) => {
      if (cancelled || !entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= .35)) return;
      observer.disconnect();
      if (rail.scrollWidth <= rail.clientWidth + 4 || typeof rail.animate !== "function") return;
      rail.style.setProperty("scroll-snap-type", "none");
      animation = rail.animate(
        [
          { transform: "translate3d(0, 0, 0)", easing: "cubic-bezier(.37,0,.63,1)" },
          { transform: "translate3d(-18px, 0, 0)", offset: .22, easing: "cubic-bezier(.37,0,.63,1)" },
          { transform: "translate3d(0, 0, 0)", offset: .43, easing: "cubic-bezier(.37,0,.63,1)" },
          { transform: "translate3d(-14px, 0, 0)", offset: .7, easing: "cubic-bezier(.37,0,.63,1)" },
          { transform: "translate3d(0, 0, 0)" },
        ],
        { duration: 1400, delay: 350, easing: "linear", iterations: 1 },
      );
      const restoreSnap = () => rail.style.removeProperty("scroll-snap-type");
      if (animation.finished?.finally) animation.finished.finally(restoreSnap).catch(() => {});
      else window.setTimeout(restoreSnap, 1900);
    }, { threshold: [.35] });
    observer.observe(rail);
  };

  const primeGalleryMain = (gallery, { urgent = false } = {}) => {
    const mainImage = gallery?.querySelector("[data-gallery-main]");
    const activeControl = gallery?.querySelector('[data-gallery-thumb][aria-pressed="true"]');
    if (!mainImage || !activeControl) return Promise.resolve();
    mainImage.loading = "eager";
    if ("fetchPriority" in mainImage) mainImage.fetchPriority = urgent ? "high" : "low";
    return loadGalleryImage(galleryControlSources(activeControl));
  };

  const productGallery = document.querySelector("[data-product-gallery][data-gallery]");
  if (productGallery) {
    const primeFromIntent = () => {
      primeGalleryMain(productGallery, { urgent: true })
        .then(() => {
          if (!reducedData) warmGalleryNeighbors(productGallery);
        })
        .catch(() => {});
    };
    document.querySelectorAll('[data-purchase-cta][data-cta-destination="product_gallery"]').forEach((cta) => {
      cta.addEventListener("pointerdown", primeFromIntent, { passive: true });
      cta.addEventListener("focus", primeFromIntent, { passive: true });
      cta.addEventListener("click", primeFromIntent, { passive: true });
    });

  }

  document.querySelectorAll("[data-gallery]").forEach((gallery) => {
    const controls = [...gallery.querySelectorAll("[data-gallery-thumb]")];
    controls.forEach((button) => {
      button.disabled = false;
      button.addEventListener("click", (event) => {
        selectGalleryControl(gallery, button, event, { interactionSource: "thumbnail" });
      });
    });
    initializeGallerySwipe(gallery, controls);
    initializeGalleryRailHint(gallery);
    if (reducedData) return;
    const warm = () => warmGalleryNeighbors(gallery);
    let warmScheduled = false;
    const scheduleWarm = () => {
      if (warmScheduled) return;
      warmScheduled = true;
      const runWhenIdle = () => {
        window.requestAnimationFrame(() => {
          if ("requestIdleCallback" in window) window.requestIdleCallback(warm, { timeout: 1500 });
          else window.setTimeout(warm, 0);
        });
      };
      const mainImage = gallery.querySelector("[data-gallery-main]");
      if (typeof mainImage?.decode === "function") {
        mainImage.decode().catch(() => {}).then(runWhenIdle);
      } else runWhenIdle();
    };
    if (!("IntersectionObserver" in window)) {
      scheduleWarm();
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      scheduleWarm();
    }, { rootMargin: "400px 0px" });
    observer.observe(gallery);
  });


  document.querySelectorAll("[data-mechanism-switcher]").forEach((switcher) => {
    const panels = [...switcher.querySelectorAll("video[data-mechanism-panel]")];
    const tabs = [...switcher.querySelectorAll("[data-mechanism-tab]")];
    const label = switcher.querySelector("[data-mechanism-label]");
    const summaryTitle = switcher.querySelector("[data-mechanism-summary-title]");
    const summaryCopy = switcher.querySelector("[data-mechanism-summary-copy]");
    if (!panels.length || panels.length !== tabs.length) return;

    tabs.forEach((tab) => { tab.disabled = false; });
    panels.forEach((panel) => {
      panel.muted = true;
      panel.loop = true;
      panel.playsInline = true;
      panel.pause();
    });

    let activeIndex = 0;
    let inView = false;
    let playbackEnabled = !reducedMotion && !reducedData;
    let nativeControlPanel = null;
    let nativeControlTimer = 0;
    const currentPanel = () => panels[activeIndex];
    const playbackRequested = () => playbackEnabled && !reducedMotion;
    const playCurrent = () => {
      const panel = currentPanel();
      if (playbackRequested() && !document.hidden && inView && isVisible(panel)) safePlay(panel);
      else panel.pause();
    };
    const activate = (index) => {
      activeIndex = (index + panels.length) % panels.length;
      panels.forEach((panel, panelIndex) => {
        const active = panelIndex === activeIndex;
        panel.pause();
        panel.classList.toggle("is-current", active);
        panel.setAttribute("aria-hidden", String(!active));
        panel.tabIndex = active ? 0 : -1;
      });
      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === activeIndex;
        tab.classList.toggle("is-current", active);
        tab.closest("li")?.classList.toggle("is-current", active);
        tab.setAttribute("aria-pressed", String(active));
        tab.removeAttribute("aria-selected");
        tab.tabIndex = 0;
      });
      if (label) label.textContent = tabs[activeIndex].dataset.mechanismLabel || tabs[activeIndex].textContent.trim();
      if (summaryTitle) summaryTitle.textContent = tabs[activeIndex].querySelector("strong")?.textContent || "";
      if (summaryCopy) summaryCopy.textContent = tabs[activeIndex].querySelector("span")?.textContent || "";
      playCurrent();
      document.dispatchEvent(new CustomEvent("eagleline:media-state"));
    };

    panels.forEach((panel) => {
      const markNativeControlIntent = () => {
        nativeControlPanel = panel;
        window.clearTimeout(nativeControlTimer);
        nativeControlTimer = window.setTimeout(() => { nativeControlPanel = null; }, 800);
      };
      panel.addEventListener("pointerdown", markNativeControlIntent, { passive: true });
      panel.addEventListener("keydown", markNativeControlIntent);
      panel.addEventListener("play", () => {
        if (nativeControlPanel === panel) playbackEnabled = true;
      });
      panel.addEventListener("pause", () => {
        if (nativeControlPanel === panel) playbackEnabled = false;
      });
    });
    tabs.forEach((tab, index) => tab.addEventListener("click", (event) => {
      if (index === activeIndex) return;
      if (event.isTrusted) {
        publishEagleLineEvent("eagleline_media_engage", {
          media_type: "video",
          placement: "mid_page",
          action: "gallery_select",
        });
      }
      activate(index);
    }));
    observeVisibility(
      switcher,
      (visible) => {
        inView = visible;
        if (visible) playCurrent();
        else panels.forEach((panel) => panel.pause());
      },
      { threshold: 0.32 },
    );
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) panels.forEach((panel) => panel.pause());
      else playCurrent();
    });
    document.addEventListener("eagleline:media-state", playCurrent);
    switcher.addEventListener("keydown", (event) => {
      if (!tabs.includes(document.activeElement)) return;
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      const direction = event.key === "ArrowRight" ? 1 : -1;
      const index = (tabs.indexOf(document.activeElement) + direction + tabs.length) % tabs.length;
      tabs[index].focus();
      if (event.isTrusted) {
        publishEagleLineEvent("eagleline_media_engage", {
          media_type: "video",
          placement: "mid_page",
          action: "gallery_select",
        });
      }
      activate(index);
    });
    onMediaChange(motionPreference, (event) => {
      playbackEnabled = !event.matches && !reducedData;
      if (event.matches) {
        panels.forEach((panel) => panel.pause());
      } else playCurrent();
    });
    activate(0);
  });

  document.querySelectorAll("[data-product-form]").forEach((root) => {
    const productScript = root.querySelector("[data-product-json]");
    if (!productScript) return;

    let product;
    try {
      product = JSON.parse(productScript.textContent);
    } catch {
      return;
    }

    const form = root.querySelector("form");
    const variantSelector = root.querySelector("[data-variant-id]");
    const price = root.querySelector("[data-product-price]");
    const referencePrice = root.querySelector("[data-product-reference-price]");
    const discount = root.querySelector("[data-product-discount]");
    const shippingSummary = root.querySelector("[data-shipping-summary]");
    const status = root.querySelector("[data-variant-status]");
    const acceleratedCheckout = root.querySelector("[data-accelerated-checkout]");
    const checkoutDivider = root.querySelector("[data-checkout-divider]");
    const directCheckout = root.querySelector("[data-direct-checkout]");
    const directCheckoutText = root.querySelector("[data-direct-checkout-text]");
    const selectionGates = [...root.querySelectorAll("[data-selection-gate]")];
    const gallery = root.closest(".el-product-grid")?.querySelector("[data-gallery]");
    const optionControls = root.querySelector("[data-option-controls]");
    const optionButtons = [...root.querySelectorAll("[data-option-control]")];
    const packOptions = root.querySelector(".el-pack-options");
    const colorOptions = root.querySelector("[data-color-options]");
    const strings = product.strings || {};
    const promotionVisible = product.promotionVisible === true || root.dataset.promotionVisible === "true";
    const locale = product.locale || document.documentElement.lang || undefined;
    const currency = product.currency || "";
    let currencyFormatter = null;
    try {
      if (currency) currencyFormatter = new Intl.NumberFormat(locale, { style: "currency", currency });
    } catch {
      currencyFormatter = null;
    }
    const money = (cents, formatted = "") => (
      formatted
      || currencyFormatter?.format(Number(cents || 0) / 100)
      || String(Number(cents || 0) / 100)
    );
    const renderString = (template, replacements = {}) => Object.entries(replacements).reduce(
      (result, [token, value]) => result.replaceAll(token, () => String(value)),
      template || "",
    );
    const displayPrice = (variant) => money(variant?.price, variant?.priceFormatted);
    const canonicalPack = (value) => value === "one_pack" || value === "two_pack" ? value : "";
    const canonicalColor = (value) => value === "black" || value === "white" || value === "mixed" ? value : "";
    let selectedPackKey = null;
    let selectedColorKey = null;
    let internalVariantChange = false;
    let pendingSelectionEvents = new Map();
    let deliberateSelections = new Set();
    let checkoutHighlighted = false;
    const choiceCompleteStorageKey = "eagleline_product_choice_complete_v1";
    let choiceCompleteStorageAvailable = false;
    let choiceCompletePublished = true;
    let pendingChoiceVariant = null;
    let choiceReadyTimer = 0;
    let choiceReadyAttempts = 0;
    try {
      const marker = window.sessionStorage.getItem(choiceCompleteStorageKey);
      choiceCompletePublished = marker === "1" || marker === "pending";
      choiceCompleteStorageAvailable = true;
    } catch {
      choiceCompleteStorageAvailable = false;
    }

    if (variantSelector && status?.id) variantSelector.setAttribute("aria-describedby", status.id);
    if (variantSelector && optionControls && optionButtons.length) {
      optionControls.hidden = false;
      variantSelector.closest(".el-option-group")?.classList.add("has-options");
      variantSelector.tabIndex = -1;
      variantSelector.hidden = true;
      variantSelector.required = false;
      if (form) form.noValidate = true;
    }

    const findVariant = () =>
      product.variants.find((variant) => String(variant.id) === variantSelector?.value);
    const findVariantByKeys = (packKey, colorKey) =>
      product.variants.find((variant) => variant.packKey === packKey && variant.colorKey === colorKey);
    const findOfferVariantForPack = (packKey) =>
      product.variants.find((variant) => variant.packKey === packKey && variant.available)
      || product.variants.find((variant) => variant.packKey === packKey);
    const selectionPrompt = () => {
      if (!selectedPackKey && !selectedColorKey) return strings.selectionPrompt || "";
      if (!selectedPackKey) return strings.selectionPack || "";
      if (!selectedColorKey) return strings.selectionColor || "";
      return strings.selectionCheck || "";
    };

    const syncOptionControls = () => {
      if (packOptions) packOptions.dataset.selected = selectedPackKey || "";
      if (colorOptions) {
        colorOptions.dataset.pack = selectedPackKey || "";
        colorOptions.dataset.selected = selectedColorKey || "";
      }
      optionButtons.forEach((button) => {
        const name = button.dataset.optionName;
        const value = button.dataset.optionValue;
        const candidatePackKey = name === "pack" ? canonicalPack(value) : selectedPackKey;
        const candidateColorKey = name === "color" ? canonicalColor(value) : null;
        const candidateAvailable = product.variants.some((variant) => (
          variant.available
          && (!candidatePackKey || variant.packKey === candidatePackKey)
          && (!candidateColorKey || variant.colorKey === candidateColorKey)
        ));
        const active = name === "pack" ? value === selectedPackKey : value === selectedColorKey;
        const mixedHidden = name === "color" && value === "mixed" && selectedPackKey !== "two_pack";
        button.hidden = mixedHidden;
        button.classList.toggle("is-selected", active);
        button.setAttribute("aria-pressed", String(active));
        button.disabled = !candidateAvailable;
        if (mixedHidden) button.disabled = true;
        if (name !== "color") return;

        const label = button.querySelector("[data-color-label]");
        const labelKey = selectedPackKey === "one_pack"
          ? { black: "colorBlackOne", white: "colorWhiteOne" }[value]
          : selectedPackKey === "two_pack"
            ? { black: "colorBlackTwo", white: "colorWhiteTwo", mixed: "colorMixedTwo" }[value]
            : { black: "colorBlack", white: "colorWhite", mixed: "colorMixed" }[value];
        const datasetLabel = selectedPackKey === "one_pack"
          ? button.dataset.colorLabelOne
          : selectedPackKey === "two_pack"
            ? button.dataset.colorLabelTwo
            : button.dataset.colorLabelBase;
        if (label) label.textContent = labelKey && strings[labelKey] ? strings[labelKey] : datasetLabel || label.textContent;

      });
    };

    const syncPrice = (variant) => {
      const offerVariant = variant || findOfferVariantForPack(selectedPackKey || "one_pack");
      if (!offerVariant) return;
      if (price) price.textContent = displayPrice(offerVariant);
      const fullPrice = Number(offerVariant.packFullPrice || offerVariant.compareAtPrice || 0);
      const hasReferencePrice = fullPrice > Number(offerVariant.price || 0);
      if (referencePrice) {
        referencePrice.hidden = !hasReferencePrice;
        const configuredReference = offerVariant.packKey === "two_pack"
          ? referencePrice.dataset.referenceTwo
          : referencePrice.dataset.referenceOne;
        referencePrice.textContent = hasReferencePrice
          ? money(
            fullPrice,
            (promotionVisible ? configuredReference : "") || (fullPrice === Number(offerVariant.compareAtPrice || 0)
              ? offerVariant.compareAtPriceFormatted
              : ""),
          )
          : "";
      }
      if (discount) {
        const discountLabel = offerVariant.packKey === "two_pack"
          ? strings.discountTwo || discount.dataset.discountTwo
          : strings.discountOne || discount.dataset.discountOne;
        discount.hidden = !promotionVisible || !discountLabel;
        discount.textContent = promotionVisible ? discountLabel || "" : "";
      }
    };

    const selectGalleryColor = (colorKey, userInitiated = false) => {
      if (!gallery) return;
      const galleryColor = canonicalColor(colorKey);
      const matching = gallery.querySelector(`[data-gallery-primary-color="${galleryColor}"]`);
      if (matching) {
        selectGalleryControl(gallery, matching, null, {
          autoplay: false,
          customerSelection: Boolean(userInitiated),
          instant: true,
          interactionSource: "color_auto",
        });
      }
    };

    const updateGallery = (variant, changeDetail = null) => {
      if (!gallery) return;
      gallery.dataset.selectedVariantId = String(variant.id);
      gallery.dispatchEvent(
        new CustomEvent("eagleline:variant-change", {
          bubbles: true,
          detail: { variant, userInitiated: Boolean(changeDetail?.userInitiated) },
        }),
      );
    };

    const syncPurchaseState = (variant) => {
      const complete = Boolean(selectedPackKey && selectedColorKey);
      const hasPaymentControl = Boolean(acceleratedCheckout || directCheckout);
      const ready = Boolean(complete && variant?.available && hasPaymentControl);
      root.dataset.selectionReady = String(ready);
      if (acceleratedCheckout) {
        acceleratedCheckout.dataset.variantId = ready ? String(variant.id) : "";
        acceleratedCheckout.setAttribute("aria-disabled", String(!ready));
        acceleratedCheckout.setAttribute("aria-hidden", String(!ready));
        acceleratedCheckout.toggleAttribute("inert", !ready);
        acceleratedCheckout.classList.toggle("is-selection-incomplete", !ready);
      }
      if (checkoutDivider) checkoutDivider.hidden = false;
      if (directCheckout) {
        directCheckout.dataset.variantId = ready ? String(variant.id) : "";
        directCheckout.setAttribute("aria-disabled", String(!ready));
        directCheckout.setAttribute("aria-hidden", String(!ready));
        directCheckout.toggleAttribute("inert", !ready);
        directCheckout.classList.toggle("is-selection-incomplete", !ready);
        if (ready && variant.checkoutUrl) directCheckout.href = variant.checkoutUrl;
        else directCheckout.removeAttribute("href");
      }
      selectionGates.forEach((selectionGate) => {
        selectionGate.hidden = ready;
        selectionGate.setAttribute("aria-label", ready ? "" : complete ? strings.selectionSoldOut || "" : selectionPrompt());
      });
      if (directCheckoutText) {
        directCheckoutText.textContent = complete && !variant?.available
          ? strings.soldOut || ""
          : strings.checkout || "";
      }
      return ready;
    };

    const updateOfferDetails = (variant) => {
      if (shippingSummary && variant) {
        const shippingCopy = variant.packKey === "one_pack"
          ? renderString(strings.onePackShipping, {
            __EL_SHIPPING__: money(variant.shippingPrice, variant.shippingPriceFormatted),
          })
          : variant.packKey === "two_pack"
            ? strings.twoPackShipping || ""
            : "";
        shippingSummary.textContent = shippingCopy;
        shippingSummary.classList.toggle("is-free-shipping", shippingCopy === strings.twoPackShipping);
      }
    };

    const dispatchPendingChoice = () => {
      choiceReadyTimer = 0;
      if (!pendingChoiceVariant || !choiceCompleteStorageAvailable || choiceCompletePublished) return;
      if (typeof window.Shopify?.analytics?.publish !== "function") {
        // Only wait for the existing Shopify publisher. Never retry a dispatched
        // event or bypass Shopify's consent-managed subscriber pipeline.
        if (choiceReadyAttempts++ < 20) choiceReadyTimer = window.setTimeout(dispatchPendingChoice, 250);
        return;
      }
      try {
        const marker = window.sessionStorage.getItem(choiceCompleteStorageKey);
        if (marker === "1" || marker === "pending") {
          choiceCompletePublished = true;
          return;
        }
        window.sessionStorage.setItem(choiceCompleteStorageKey, "pending");
        if (window.sessionStorage.getItem(choiceCompleteStorageKey) !== "pending") throw new Error("Storage unavailable");
      } catch {
        choiceCompleteStorageAvailable = false;
        return;
      }
      choiceCompletePublished = true;
      const variant = pendingChoiceVariant;
      pendingChoiceVariant = null;
      publishEagleLineEvent("eagleline_product_choice_complete", {
        variant_id: String(variant.id),
        pack_size: canonicalPack(variant.packKey),
        color: canonicalColor(variant.colorKey),
      }, (accepted) => {
        try {
          window.sessionStorage.setItem(choiceCompleteStorageKey, accepted ? "1" : "");
          choiceCompletePublished = accepted;
        } catch {
          // Keep the reservation on an uncertain write; do not duplicate it.
          choiceCompleteStorageAvailable = false;
        }
      });
    };

    const publishChoiceComplete = (variant, ready, changeDetail) => {
      if (!ready || !deliberateSelections.has("pack") || !deliberateSelections.has("color")) {
        pendingChoiceVariant = null;
        window.clearTimeout(choiceReadyTimer);
        choiceReadyTimer = 0;
        return;
      }
      if (!ready || !variant?.available || !changeDetail?.userInitiated || analyticsPreview) return;
      if (!choiceCompleteStorageAvailable || choiceCompletePublished) return;
      pendingChoiceVariant = variant;
      if (!choiceReadyTimer) dispatchPendingChoice();
    };

    const publishPendingSelections = (variant) => {
      if (!variant?.available || !pendingSelectionEvents.size) return;
      const events = [...pendingSelectionEvents.values()];
      pendingSelectionEvents.clear();
      events.forEach((changeDetail) => {
        publishEagleLineEvent("eagleline_option_select", {
          option: changeDetail.option === "pack" ? "pack_size" : "color",
          value: changeDetail.option === "pack"
            ? canonicalPack(changeDetail.value)
            : canonicalColor(changeDetail.value),
          variant_id: String(variant.id),
          pack_size: canonicalPack(variant.packKey),
          color: canonicalColor(variant.colorKey),
        });
      });
    };

    const updateSelection = (changeDetail = null, syncSelector = true) => {
      const variant = selectedPackKey && selectedColorKey
        ? findVariantByKeys(selectedPackKey, selectedColorKey)
        : null;
      const nextVariantId = variant ? String(variant.id) : "";
      if (syncSelector && variantSelector && variantSelector.value !== nextVariantId) {
        internalVariantChange = true;
        variantSelector.value = nextVariantId;
        variantSelector.dispatchEvent(new Event("change", { bubbles: true }));
        internalVariantChange = false;
      }
      syncOptionControls();
      const selectedPackOffer = selectedPackKey ? findOfferVariantForPack(selectedPackKey) : null;
      syncPrice(variant || selectedPackOffer);
      updateOfferDetails(variant || selectedPackOffer);
      if (status) {
        status.textContent = !selectedPackKey || !selectedColorKey
          ? selectionPrompt()
          : variant?.available
          ? renderString(strings.statusAvailable, {
            __EL_TITLE__: variant.title,
            __EL_PRICE__: displayPrice(variant),
          })
          : renderString(strings.statusSoldOut, {
            __EL_TITLE__: variant?.title || strings.selectedCombination || "",
          });
        status.classList.toggle("is-available", Boolean(variant?.available));
      }
      if (variant?.available) {
        updateGallery(variant, changeDetail);
        if (changeDetail?.userInitiated && !checkoutHighlighted && !reducedMotion && directCheckout?.animate) {
          checkoutHighlighted = true;
          directCheckout.animate(
            [
              { boxShadow: "0 0 0 0 rgba(7,87,213,0)" },
              { boxShadow: "0 0 0 4px rgba(7,87,213,.18)", offset: .45 },
              { boxShadow: "0 0 0 0 rgba(7,87,213,0)" },
            ],
            { duration: 560, easing: "ease-out", iterations: 1 },
          );
        }
        publishPendingSelections(variant);
      }
      const ready = syncPurchaseState(variant);
      publishChoiceComplete(variant, ready, changeDetail);
    };

    variantSelector?.addEventListener("change", () => {
      if (internalVariantChange) return;
      const variant = findVariant();
      selectedPackKey = canonicalPack(variant?.packKey) || null;
      selectedColorKey = canonicalColor(variant?.colorKey) || null;
      pendingSelectionEvents.clear();
      deliberateSelections.clear();
      if (selectedColorKey) selectGalleryColor(selectedColorKey, false);
      updateSelection(null, false);
    });
    optionButtons.forEach((button) => {
      button.addEventListener("click", (event) => {
        if (!variantSelector || button.disabled) return;
        const option = button.dataset.optionName;
        const value = button.dataset.optionValue;
        const currentValue = option === "pack" ? selectedPackKey : selectedColorKey;
        if (value === currentValue) return;
        if (option === "pack") {
          selectedPackKey = canonicalPack(value) || null;
          if (selectedColorKey && !findVariantByKeys(selectedPackKey, selectedColorKey)?.available) {
            selectedColorKey = null;
            pendingSelectionEvents.delete("color");
            deliberateSelections.delete("color");
          }
        }
        else selectedColorKey = canonicalColor(value) || null;
        const changeDetail = { option, value, userInitiated: event.isTrusted };
        if (event.isTrusted) {
          pendingSelectionEvents.set(option, changeDetail);
          deliberateSelections.add(option);
        }
        if (option === "color") selectGalleryColor(value, event.isTrusted);
        updateSelection(changeDetail);
      });
    });
    const initialVariant = findVariant();
    selectedPackKey = canonicalPack(initialVariant?.packKey) || null;
    selectedColorKey = canonicalColor(initialVariant?.colorKey) || null;
    if (selectedColorKey) selectGalleryColor(selectedColorKey, false);
    updateSelection(null, false);

    window.addEventListener("pageshow", () => {
      window.requestAnimationFrame(() => {
        const restoredVariant = findVariant();
        const selectedVariant = selectedPackKey && selectedColorKey
          ? findVariantByKeys(selectedPackKey, selectedColorKey)
          : null;
        if ((restoredVariant?.id || null) === (selectedVariant?.id || null)) return;
        selectedPackKey = canonicalPack(restoredVariant?.packKey) || null;
        selectedColorKey = canonicalColor(restoredVariant?.colorKey) || null;
        pendingSelectionEvents.clear();
        deliberateSelections.clear();
        if (selectedColorKey) selectGalleryColor(selectedColorKey, false);
        updateSelection(null, false);
      });
    });

  });

  /* FAQ accordion: a smooth height tween on top of the native <details>
     toggle. No-JS keeps the instant native behavior; reduced motion skips
     the controller entirely. The .is-animating class clips the box while
     the tween runs. */
  if (!reducedMotion) {
    document.querySelectorAll(".el-v6-support-list details").forEach((details) => {
      const summary = details.querySelector("summary");
      if (!summary) return;
      summary.addEventListener("click", (event) => {
        event.preventDefault();
        if (details.dataset.animating) return;
        const summaryHeight = summary.offsetHeight;
        const options = { duration: 320, easing: "cubic-bezier(.22, 1, .36, 1)" };
        details.dataset.animating = "1";
        details.classList.add("is-animating");
        if (details.open) {
          const start = details.offsetHeight;
          const animation = details.animate({ height: [start + "px", summaryHeight + "px"] }, options);
          animation.onfinish = () => {
            details.open = false;
            delete details.dataset.animating;
            details.classList.remove("is-animating");
          };
        } else {
          details.open = true;
          const end = details.offsetHeight;
          const animation = details.animate({ height: [summaryHeight + "px", end + "px"] }, options);
          animation.onfinish = () => {
            delete details.dataset.animating;
            details.classList.remove("is-animating");
          };
        }
      });
    });
  }
  };

  /* On compact screens, let the responsive hero image reach first paint
     before initializing below-fold motion and controls. Essential content,
     links, and native form fallbacks already work in the HTML. */
  const compactBoot = window.matchMedia("(max-width: 920px)").matches;
  if (compactBoot) {
    window.requestAnimationFrame(() => window.setTimeout(initialize, 0));
  } else {
    initialize();
  }
})();
